import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ICommand, ICommandHistory } from '../image-editor/domain/interfaces/command';

export class CommandHistory implements ICommandHistory {
  private readonly commands: ICommand[] = [];

  private index = -1;

  private readonly dirty$ = new Subject<boolean>();

  push(command: ICommand): void {
    if (!command) throw new Error('Null commands is not allowed.');
    if (this.canRedo()) {
      this.commands.splice(
        this.index + 1,
        this.commands.length - this.index - 1
      );
    }
    this.commands.push(command);
    this.index++;
    command.execute();
    this.dirty$.next(true);
  }

  head(): ICommand | null {
    return this.index < 0 ? null : this.commands[this.index];
  }

  canUndo(): boolean {
    return this.index >= 0;
  }

  canRedo(): boolean {
    return this.index < this.commands.length - 1;
  }

  undo(): void {
    if (!this.canUndo())
      throw new Error('An Undo operation cannot be executed.');
    this.head()?.rollback();
    this.index--;
    this.dirty$.next(this.canUndo());
  }

  redo(): void {
    if (!this.canRedo())
      throw new Error('A Redo operation cannot be executed.');
    this.index++;
    this.head()?.execute();
    this.dirty$.next(true);
  }

  clear(): void {
    this.index = -1;
    this.commands.splice(0, this.commands.length);
  }

  reset(): void {
    while (this.index >= 0) {
      this.head()?.rollback();
      this.index--;
    }
    this.clear();
    this.dirty$.next(false);
  }

  getAll(): ICommand[] {
    if (this.index < 0) return [];
    return this.commands.slice(0, this.index + 1);
  }

  size(): number {
    return this.commands.length;
  }

  dirty(): Observable<boolean> {
    return this.dirty$.pipe(distinctUntilChanged());
  }
}
